import { useState } from 'react';
import type { ConversationSla } from '@repletoryreach/sdk-api/inbox';
import { useInterval } from '@repletoryreach/sdk-utils/hooks';
import { StatusBadge, type StatusTone } from '@repletoryreach/sdk-ui/data-display';
import { useT } from '../../../i18n/I18nProvider';

/**
 * W9: SLA badge maths. The active window is the first-response deadline until
 * a first response is recorded, then (if the policy defines one) the
 * resolution deadline. Amber from 80% of the window elapsed, red once the
 * deadline passes (or the server has stamped `breached_at`).
 */
export type SlaPhase = 'first_response' | 'resolution' | 'breached' | 'met';

export interface SlaState {
  phase: SlaPhase;
  tone: StatusTone;
  /** ms until the active deadline: negative when overdue. */
  remainingMs: number;
  /** Elapsed fraction of the active window (may exceed 1). */
  ratio: number;
  /** ISO deadline of the active window, null once met. */
  deadline: string | null;
}

export const SLA_AMBER_RATIO = 0.8;

function slaWindow(startIso: string, dueIso: string, nowMs: number, breachedStamp: boolean): Omit<SlaState, 'phase'> & { breached: boolean } {
  const start = Date.parse(startIso);
  const due = Date.parse(dueIso);
  const remainingMs = due - nowMs;
  const breached = breachedStamp || remainingMs <= 0;
  const ratio = due > start ? (nowMs - start) / (due - start) : 1;
  const tone: StatusTone = breached ? 'down' : ratio >= SLA_AMBER_RATIO ? 'warning' : 'healthy';
  return { tone, remainingMs, ratio, deadline: dueIso, breached };
}

export function deriveSla(sla: ConversationSla | null | undefined, nowMs: number): SlaState | null {
  if (!sla) return null;
  if (!sla.first_response_at) {
    const w = slaWindow(sla.started_at, sla.first_response_due_at, nowMs, !!sla.breached_at);
    return { phase: w.breached ? 'breached' : 'first_response', tone: w.tone, remainingMs: w.remainingMs, ratio: w.ratio, deadline: w.deadline };
  }
  if (sla.resolve_due_at && !sla.resolved_at) {
    const w = slaWindow(sla.started_at, sla.resolve_due_at, nowMs, false);
    return { phase: w.breached ? 'breached' : 'resolution', tone: w.tone, remainingMs: w.remainingMs, ratio: w.ratio, deadline: w.deadline };
  }
  return { phase: 'met', tone: 'healthy', remainingMs: 0, ratio: 1, deadline: null };
}

/** "45s" · "12m" · "3h 05m" · "2d 4h": coarse enough to read at a glance. */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  if (total < 60) return `${total}s`;
  const m = Math.floor(total / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${String(m % 60).padStart(2, '0')}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

/** One shared clock for every badge on the page (one interval, not one per row). */
export function useNow(intervalMs = 15_000): number {
  const [now, setNow] = useState(() => Date.now());
  useInterval(() => setNow(Date.now()), intervalMs);
  return now;
}

export interface SlaBadgeProps {
  sla?: ConversationSla | null;
  now: number;
  size?: 'xs' | 'sm';
  /** Row variant: time only, no phase word. */
  compact?: boolean;
  /**
   * Header variant: a short verdict on one line with the time under it.
   *
   * "First reply · 8h 09m left" as a single badge is the widest thing in the
   * thread header and pushed the assignee onto a second row. Split, it says
   * the same thing in the width the reference gives it.
   */
  stacked?: boolean;
}

export function SlaBadge({ sla, now, size = 'xs', compact, stacked }: SlaBadgeProps) {
  const t = useT();
  const s = deriveSla(sla, now);
  if (!s) return null;

  if (stacked) {
    const verdict = s.phase === 'breached' ? t('inbox.sla.breached')
      : s.phase === 'met' ? t('inbox.sla.met')
      : s.tone === 'warning' ? t('inbox.sla.dueSoon')
      : t('inbox.sla.onTrack');
    const time = s.phase === 'met' ? null
      : s.phase === 'breached' ? `${formatDuration(-s.remainingMs)} ${t('inbox.sla.over')}`
      : `${formatDuration(s.remainingMs)} ${t('inbox.sla.left')}`;
    return (
      <span className="rr-inbox2__sla rr-inbox2__sla--stacked">
        <StatusBadge status={s.tone} size="xs">{verdict}</StatusBadge>
        {time && <span className="rr-inbox2__sla-time">{time}</span>}
      </span>
    );
  }

  let label: string;
  if (s.phase === 'breached') {
    const over = `${formatDuration(-s.remainingMs)} ${t('inbox.sla.over')}`;
    label = compact ? over : `${t('inbox.sla.breached')} · ${over}`;
  } else if (s.phase === 'met') {
    label = t('inbox.sla.met');
  } else {
    const left = `${formatDuration(s.remainingMs)} ${t('inbox.sla.left')}`;
    const phase = s.phase === 'first_response' ? t('inbox.sla.firstReply') : t('inbox.sla.resolve');
    label = compact ? left : `${phase} · ${left}`;
  }

  const title = s.deadline ? `${t('inbox.sla.due')} ${new Date(s.deadline).toLocaleString()}` : t('inbox.sla.met');
  return (
    <span title={title} className="rr-inbox2__sla">
      <StatusBadge status={s.tone} size={size} withDot={!compact}>{label}</StatusBadge>
    </span>
  );
}
